import { defineStore } from 'pinia'
import type { User } from '@/data/schema/user'
import type { Vehicle } from '@/data/schema/vehicle'
import DriverService from '@/services/driver'
import useToasterStore from './toaster'

export default defineStore('auth', {
	// State
	state: () => ({
		user: null as User | null,
		masterUser: null as User | null,
		impersonateUserId: null as string | null,
		accessToken: null as string | null,
		accessTokenExpiry: '' as string,
		idToken: null as string | null,
		isLoggedIn: false,
		authenticationInProgress: false,
		vehicleLocked: false
	}),

	// Getters
	getters: {
		isUserLoggedIn: (state) => state.accessToken !== null,

		tokenHasExpired: (state) => {
			const now = new Date()
			const expiryDate = new Date(state.accessTokenExpiry)
			return expiryDate < now
		},

		vehicle: (state) => state.user?.current_vehicle,

		/**
		 * Permission check (PURE, no state mutation)
		 */
		isAbleTo() {
			return (permissions: string[]) => {
				if (!this.user) return false
				if (!Array.isArray(this.user.permissions)) return false

				if (permissions.length === 0) return true

				return permissions.some((p) => this.user!.permissions.includes(p))
			}
		},

		/**
		 * Role check
		 */
		hasRole() {
			return (roles: string[]) => {
				if (!this.user) return false
				if (!Array.isArray(this.user.roles)) return false

				if (roles.length === 0) return true

				return roles.some((r) => this.user!.roles!.includes(r))
			}
		}
	},

	// Actions
	actions: {
		setLoggedIn(value: boolean) {
			this.isLoggedIn = value
		},

		logout() {
			this.setUser(null)
			this.setTokens(null, null)
			this.setLoggedIn(false)
			window.location.replace('.auth/logout')
		},

		setTokens(accessToken: string | null, idToken: string | null) {
			this.accessToken = accessToken
			this.idToken = idToken
		},

		setDepot(depot: string) {
			const now = new Date()
			const expiry = 3600000 // 1 hour

			if (this.user) {
				this.user.current_depot = depot
				localStorage.setItem('depot', depot)
				localStorage.setItem('depotExpiry', String(now.getTime() + expiry))
			}
		},

		/**
		 * Set current driver vehicle
		 * @param vehicle Vehicle object | null
		 */
		setVehicle(vehicle: Vehicle | null) {
			const api = new DriverService()
			const toaster = useToasterStore()
			const now = new Date()
			const expiry = 43200000 // 12 hours

			if (this.user) {
				if (vehicle === null && this.vehicle) {
					toaster.working({ text: 'Clearing vehicle selection...' })

					api.removeVehicle(this.vehicle.registration)
						.then(() => {
							toaster.clear()
							localStorage.removeItem('vehicle')
							localStorage.removeItem('vehicleExpiry')
							this.vehicleLocked = false
						})
						.catch((error) => {
							console.error(error)
							toaster.clear()
							toaster.error({ text: error.response.data.data.error })
						})
				} else if (vehicle) {
					toaster.working({ text: 'Setting vehicle selection...' })

					api.confirmVehicle(vehicle.registration)
						.then(() => {
							toaster.clear()
							localStorage.setItem('vehicle', JSON.stringify(vehicle))
							localStorage.setItem('vehicleExpiry', String(now.getTime() + expiry))
							this.vehicleLocked = true
						})
						.catch((error) => {
							console.error(error)
							toaster.clear()
							toaster.error({ text: error.response.data.data.error })
						})
				}

				this.user.current_vehicle = vehicle
			}
		},

		setUser(user: any | null) {
			const now = new Date()
			const depotExpiry = localStorage.getItem('depotExpiry')
				? Number(localStorage.getItem('depotExpiry'))
				: null
			const vehicleExpiry = localStorage.getItem('vehicleExpiry')
				? Number(localStorage.getItem('vehicleExpiry'))
				: null
			this.user = user

			// 1. Look for current_depot value
			// 2. Check if valid (not expired) depot stored in localStorage & user has stored depot in assigned_depots
			// 3. Either use valid localStorage depot or get first value from assigned_depots
			if (this.user && this.user?.current_depot === undefined) {
				if (
					localStorage.getItem('depot') &&
					depotExpiry &&
					now.getTime() < depotExpiry &&
					user.assigned_depots.includes(localStorage.getItem('depot'))
				) {
					this.user!.current_depot = localStorage.getItem('depot')!
				} else {
					this.setDepot(user.assigned_depots[0])
				}
			}

			// 1. Look for current_vehicle value
			// 2. Check if valid (not expired) vehicle stored in localStorage
			if (this.user && this.user.current_vehicle === undefined) {
				if (
					localStorage.getItem('vehicle') &&
					vehicleExpiry &&
					now.getTime() < vehicleExpiry
				) {
					this.user.current_vehicle = JSON.parse(localStorage.getItem('vehicle')!)
					this.vehicleLocked = true
				} else {
					this.user.current_vehicle = null
					this.vehicleLocked = false
				}
			}
		},

		setImpersonateUserId(userId: string | null) {
			this.impersonateUserId = userId
		},

		setMasterUser(user: User | null) {
			this.masterUser = user
		},

		setTestUser(
			permissions = ['view-hire-desk', 'view-yard', 'view-yard-app', 'view-journey-planner']
		) {
			this.user = {
				id: 'TEST',
				first_name: 'Test',
				name: 'Test Tool',
				initials: 'TT',
				permissions: permissions,
				current_depot: 'ABE',
				current_vehicle: null
			}
		}
	}
})
